import { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "api/axios";

function Invitations() {
  const [invitations, setInvitations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: "", email: "", ville: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchInvitations = async () => {
    try {
      const res = await api.get("/invitations");
      setInvitations(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, []);

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!form.nom || !form.email || !form.ville) {
      setError("Tous les champs sont obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/invitations", form);
      setSuccess(`Invitation envoyée à ${form.email} !`);
      setForm({ nom: "", email: "", ville: "" });
      fetchInvitations();
      setTimeout(() => setOpen(false), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette invitation ?")) {
      try {
        await api.delete(`/invitations/${id}`);
        fetchInvitations();
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems="center"
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    Invitations directeurs
                  </MDTypography>
                  <MDTypography variant="caption" color="white">
                    {invitations.length} invitation(s) envoyée(s)
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" size="small" onClick={() => setOpen(true)}>
                  <Icon>add</Icon>&nbsp;Inviter un directeur
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>
                <Grid container spacing={3}>
                  {invitations.map((inv) => (
                    <Grid item xs={12} md={6} lg={4} key={inv.id}>
                      <Card sx={{ p: 2, border: "1px solid #eee", borderRadius: 3 }}>
                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <MDTypography variant="h6" fontWeight="bold">
                            {inv.nom}
                          </MDTypography>
                          <Chip
                            label={inv.utilisee ? "Activé" : new Date(inv.expire_le) < new Date() ? "Expiré" : "En attente"}
                            size="small"
                            sx={{
                              bgcolor: inv.utilisee ? "#43a047" : new Date(inv.expire_le) < new Date() ? "#e53935" : "#fb8c00",
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5} mb={0.25}>
                          <Icon sx={{ fontSize: 15, color: "#546e7a" }}>email</Icon>
                          <MDTypography variant="body2" color="text">{inv.email}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5} mb={0.25}>
                          <Icon sx={{ fontSize: 15, color: "#546e7a" }}>location_on</Icon>
                          <MDTypography variant="body2" color="text">{inv.ville}</MDTypography>
                        </MDBox>
                        <MDBox display="flex" alignItems="center" gap={0.5} mb={1}>
                          <Icon sx={{ fontSize: 15, color: "#546e7a" }}>schedule</Icon>
                          <MDTypography variant="caption" color="text">
                            Expire le : {new Date(inv.expire_le).toLocaleDateString("fr-FR")}
                          </MDTypography>
                        </MDBox>
                        <MDButton
                          color="error"
                          size="small"
                          variant="outlined"
                          onClick={() => handleDelete(inv.id)}
                        >
                          <Icon>cancel</Icon>&nbsp;Supprimer
                        </MDButton>
                      </Card>
                    </Grid>
                  ))}
                  {invitations.length === 0 && (
                    <Grid item xs={12}>
                      <MDTypography variant="body2" color="text" textAlign="center">
                        Aucune invitation envoyée pour le moment.
                      </MDTypography>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Inviter un directeur</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom complet"
              fullWidth
              value={form.nom}
              onChange={(e) => setForm({ ...form, nom: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <TextField
              label="Ville"
              fullWidth
              value={form.ville}
              onChange={(e) => setForm({ ...form, ville: e.target.value })}
            />
            {error && (
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            )}
            {success && (
              <MDTypography variant="caption" sx={{ color: "#43a047" }}>{success}</MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setOpen(false)} color="secondary">Annuler</MDButton>
          <MDButton onClick={handleSubmit} color="info" disabled={loading}>
            {loading ? "Envoi..." : "Envoyer l'invitation"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Invitations;